<template>
  <el-submenu
    v-loading="displayLoading || allDepartments.length === 0"
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.8)"
    index="4"
    :class="[
      'dropdown-menu-box',
      'context-department',
      'px-0',
      'px-md-2',
      allDepartments.length === 0 ? 'is-disabled' : ''
    ]"
    popper-class="top-nav-box context-department-popup"
  >
    <span slot="title">
      {{ contextDepartmentTitle }}

      <font-awesome-icon :icon="buildingIcon" />
    </span>

    <el-menu-item
      v-for="(department, index) in getDepartmentsList"
      :key="department.id"
      :index="`4-${index}`"
      class="dropdown-menu-item"
      @click="handleDepartmentSelect(department)"
    >
      <template v-if="department.address && department.address.country">
        <div class="two-row-menu-item">
          <span class="top-info">{{ `${department.label} - ${department.code || '-'}` }}</span>
          <span class="bottom-info">{{ department.entity.name }}</span>
        </div>
      </template>
      <template v-else>
        {{ department.code || '-' }}
      </template>

      <template v-if="isDepartmentSelected(department.id)">
        <i class="el-icon-success" />
      </template>
    </el-menu-item>
  </el-submenu>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  Maybe,
  Department,
  SetMyDepartmentsContextQuery,
  MutationSetMyDepartmentsContextArgs,
  RemoveMyDepartmentContextQuery,
  UserRole,
  FetchMeQuery,
  AllDepartmentsQuery
} from '@/types/graphql'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import { getModule } from 'vuex-module-decorators'
import UserModule from '@/store/modules/user'
import formatDepartment from '@/lib/formatDepartment'
import { ApolloQueryResult } from 'apollo-client'

// ⁡⁢⁣⁣⓿ ⁡⁢⁣⁣𝗧𝘆𝗽𝗲𝘀⁡⁡

const ALL_DEPARTMENTS_KEY = 'all-departments'
const ALL_AS24_DEPARTMENTS_KEY = 'all-as24-departments'
const ALL_TOTAL_DEPARTMENTS_KEY = 'all-total-departments'
const ALL_CIRCLEK_DEPARTMENTS_KEY = 'all-circlek-departments'

@Component({})
export default class DepartmentSubmenu extends Vue {
  // ⁡⁢⁣⁣⁡⁢⁣⁣➊ 𝗣𝗿𝗼𝗽𝘀⁡

  // ⁡⁢⁣⁣➋ 𝗗𝗮𝘁𝗮⁡

  private userModule = getModule(UserModule, this.$store);

  private buildingIcon = faBuilding;

  private displayLoading = false

  private selectedDepartments: string[] = []

  private allDepartments: AllDepartmentsQuery['allDepartments'] = [];

  // ⁡⁢⁣⁣⁡⁢⁣⁣➌ 𝗖𝗼𝗺𝗽𝘂𝘁𝗲𝗱⁡
  private get selectedDepartmentsIds() {
    if (this.userIsAdmin && this.selectedDepartments.includes(ALL_DEPARTMENTS_KEY)) {
      return [ALL_DEPARTMENTS_KEY, ALL_AS24_DEPARTMENTS_KEY, ALL_TOTAL_DEPARTMENTS_KEY, ALL_CIRCLEK_DEPARTMENTS_KEY]
    }

    if (!this.userIsAdmin && this.selectedDepartments.includes(ALL_DEPARTMENTS_KEY)) {
      return [ALL_AS24_DEPARTMENTS_KEY, ALL_TOTAL_DEPARTMENTS_KEY, ALL_CIRCLEK_DEPARTMENTS_KEY]
    }

    return this.selectedDepartments
  }

  private get userIsAdmin() {
    return this.userModule.roles.includes(UserRole.Admin)
  }

  private get contextDepartments() {
    return this.userModule.contextDepartments
  }

  private get departmentsList() {
    return this.userModule.departmentList
  }

  private get contextDepartmentTitle() {
    // All departments cases
    if (this.allDepartments.length === this.contextDepartments?.length) {
      return this.$t('contextDepartment.allDepartments').toString()
    }

    if (this.userIsAdmin && (this.contextDepartments?.length === 0 || this.contextDepartments === null)) {
      return this.$t('contextDepartment.allDepartments').toString()
    }

    if (this.allAs24DepartmentsSaved && this.allTotalDepartmentsSaved && this.allCircleKDepartmentsSaved) {
      return this.$t('contextDepartment.allDepartments').toString()
    }

    // All AS24 departments cases
    if (this.allAs24DepartmentsSaved) {
      return this.$t('contextDepartment.allAs24Departments').toString()
    }

    // All TOTAL departments cases
    if (this.allTotalDepartmentsSaved) {
      return this.$t('contextDepartment.allTotalDepartments').toString()
    }

    // All CircleK departments cases
    if (this.allCircleKDepartmentsSaved) {
      return this.$t('contextDepartment.allCircleKDepartments').toString()
    }

    // Multiple departments cases
    let title = ''

    if (title.length === 0) {
      title = this.contextDepartments?.map((curr) => {
        return formatDepartment(curr as Maybe<Partial<Department>>)
      }).join(', ') || ''
    }

    if (title.length > 15) {
      return `${title.substring(0, 15)}...`
    }

    return title
  }

  private get as24DepartmentIds() {
    return this.allDepartments?.filter((department) => department?.entity.name === 'AS24')
      .map((department) => department?.id as string)
  }

  private get totalDepartmentIds() {
    return this.allDepartments?.filter((department) => department?.entity.name === 'TOTAL')
      .map((department) => department?.id as string)
  }

  private get circleKDepartmentIds() {
    return this.allDepartments?.filter((department) => department?.entity.name === 'CIRCLEK')
      .map((department) => department?.id as string)
  }

  private get userCanAccessAllAs24Departments() {
    return this.as24DepartmentIds?.every((departmentId) => {
      return this.departmentsList?.find((department) => department?.id === departmentId)
    })
  }

  private get userCanAccessAllTotalDepartments() {
    return this.totalDepartmentIds?.every((departmentId) => {
      return this.departmentsList?.find((department) => department?.id === departmentId)
    })
  }

  private get userCanAccessAllCircleKDepartments() {
    return this.circleKDepartmentIds?.every((departmentId) => {
      return this.departmentsList?.find((department) => department?.id === departmentId)
    })
  }

  private get getDepartmentsList() {
    const userAvailableDepartments: Pick<Department, 'id' | 'code'>[] = []

    if (this.userIsAdmin) {
      userAvailableDepartments.push({
        id: ALL_DEPARTMENTS_KEY,
        code: this.$t('contextDepartment.allDepartments').toString()
      })
    }

    if (this.userCanAccessAllAs24Departments) {
      userAvailableDepartments.push({
        id: ALL_AS24_DEPARTMENTS_KEY,
        code: this.$t('contextDepartment.allAs24Departments').toString()
      })
    }

    if (this.userCanAccessAllTotalDepartments) {
      userAvailableDepartments.push({
        id: ALL_TOTAL_DEPARTMENTS_KEY,
        code: this.$t('contextDepartment.allTotalDepartments').toString()
      })
    }

    if (this.userCanAccessAllCircleKDepartments) {
      userAvailableDepartments.push({
        id: ALL_CIRCLEK_DEPARTMENTS_KEY,
        code: this.$t('contextDepartment.allCircleKDepartments').toString()
      })
    }

    if (Array.isArray(this.departmentsList) && this.departmentsList.length > 0) {
      this.departmentsList.map((item) => {
        if (item) {
          userAvailableDepartments.push(item)
        }
      })
    }

    return userAvailableDepartments as unknown as Pick<Department, 'id' | 'code' | 'address' | 'entity' | 'label'>[]
  }

  private get allAs24DepartmentsSaved() {
    return this.as24DepartmentIds?.every((departmentId) => {
      return this.contextDepartments?.find((department) => department?.id === departmentId)
    })
  }

  private get allTotalDepartmentsSaved() {
    return this.totalDepartmentIds?.every((departmentId) => {
      return this.contextDepartments?.find((department) => department?.id === departmentId)
    })
  }

  private get allCircleKDepartmentsSaved() {
    return this.circleKDepartmentIds?.every((departmentId) => {
      return this.contextDepartments?.find((department) => department?.id === departmentId)
    })
  }

  // ⁡⁢⁣⁣➍ ⁡⁢⁣⁣𝗠𝗲𝘁𝗵𝗼𝗱𝘀⁡

  private async getAllDepartments() {
    try {
      const result: ApolloQueryResult<AllDepartmentsQuery> = await this.$apollo.query({
        query: AllDepartmentsQuery
      })

      this.allDepartments = result.data.allDepartments
    } catch (error) {
      console.error('Error @ getAllDepartments Query', error)
    }
  }

  private async setMyDepartmentsContext(departmentIds: string[]) {
    this.displayLoading = true

    const mutationName = SetMyDepartmentsContextQuery

    const mutationParams: MutationSetMyDepartmentsContextArgs = {
      departmentIds: departmentIds.map((id) => parseInt(id))
    }

    try {
      const result = this.$apollo
        .mutate({
          mutation: mutationName,
          variables: mutationParams
        })

      const { data } = await result

      if (data) {
        this.reloadWindow()
      }
    } catch (error) {
      console.error('Error @ setMyDepartmentsContext Mutation', error)
    }

    this.displayLoading = false
  }

  private async removeMyDepartmentContext() {
    this.displayLoading = true

    try {
      const result = this.$apollo
        .mutate({
          mutation: RemoveMyDepartmentContextQuery
        })

      const { data } = await result

      if (data) {
        this.reloadWindow()
      }
    } catch (error) {
      console.error('Error @ RemoveMyDepartmentContext Mutation', error)
    }

    this.displayLoading = false
  }

  private handleDepartmentSelect(department: Pick<Department, 'id'>) {
    if (
      department.id !== ALL_DEPARTMENTS_KEY &&
      department.id !== ALL_AS24_DEPARTMENTS_KEY &&
      department.id !== ALL_TOTAL_DEPARTMENTS_KEY &&
      department.id !== ALL_CIRCLEK_DEPARTMENTS_KEY
    ) {
      this.selectedDepartments = this.selectedDepartments.filter(
        (id) => (id !== ALL_DEPARTMENTS_KEY && id !== ALL_AS24_DEPARTMENTS_KEY && id !== ALL_TOTAL_DEPARTMENTS_KEY && id !== ALL_CIRCLEK_DEPARTMENTS_KEY)
      )
    }

    if (department.id === ALL_AS24_DEPARTMENTS_KEY || department.id === ALL_TOTAL_DEPARTMENTS_KEY || department.id === ALL_CIRCLEK_DEPARTMENTS_KEY) {
      this.selectedDepartments = this.selectedDepartments.filter((id) => (id === ALL_AS24_DEPARTMENTS_KEY || id === ALL_TOTAL_DEPARTMENTS_KEY || id === ALL_CIRCLEK_DEPARTMENTS_KEY))
    }

    const departmentAlreadySelected = this.selectedDepartments.includes(department?.id as string)

    if (departmentAlreadySelected) {
      this.selectedDepartments = this.selectedDepartments.filter((id) => id !== department?.id)
    } else {
      this.selectedDepartments.push(department?.id as string)
    }
  }

  public changeDepartmentContext() {
    if (!this.selectedDepartments.length) {
      return
    }

    const savedDepartments = this.contextDepartments
    const selectedDepartments = this.selectedDepartmentsIds

    const allSavedDepartmentIds = savedDepartments?.map((department) => department?.id as string)
    const allSelectedDepartmentIds = selectedDepartments.reduce((acc, department) => {
      if (department === ALL_DEPARTMENTS_KEY) {
        return [
          ...acc,
          ...this.as24DepartmentIds,
          ...this.totalDepartmentIds,
          ...this.circleKDepartmentIds
        ]
      }

      if (department === ALL_AS24_DEPARTMENTS_KEY) {
        return [
          ...acc,
          ...this.as24DepartmentIds
        ]
      }

      if (department === ALL_TOTAL_DEPARTMENTS_KEY) {
        return [
          ...acc,
          ...this.totalDepartmentIds
        ]
      }

      if (department === ALL_CIRCLEK_DEPARTMENTS_KEY) {
        return [
          ...acc,
          ...this.circleKDepartmentIds
        ]
      }

      return [
        ...acc,
        department
      ]
    }, [] as string[]).filter((value, index, array) => {
      return array.indexOf(value) === index
    })

    const selectedDepartmentsContainAllSavedDepartments = allSavedDepartmentIds?.every((savedDepartmentId) => {
      return allSelectedDepartmentIds.includes(savedDepartmentId)
    })

    const savedDepartmentsContainAllSelectedDepartments = allSelectedDepartmentIds?.every((selectedDepartmentId) => {
      return allSavedDepartmentIds?.includes(selectedDepartmentId)
    })

    if (selectedDepartmentsContainAllSavedDepartments && savedDepartmentsContainAllSelectedDepartments) {
      // Same departments selected
      return
    }

    const allDepartmentsSelected = this.allDepartments.every((department) => { return allSelectedDepartmentIds.includes(department?.id as string) })
    if (this.userIsAdmin && !allSavedDepartmentIds && allDepartmentsSelected) {
      // Also same departments selected but when user is admin the `allSavedDepartmentIds` can be undefined and it means that it has all departments selected
      return
    }

    const selectedDepartmentsContainAllDepartments = this.selectedDepartmentsIds.includes(ALL_DEPARTMENTS_KEY)

    if (selectedDepartmentsContainAllDepartments) {
      this.removeMyDepartmentContext()
      return
    }

    this.setMyDepartmentsContext(allSelectedDepartmentIds)
  }

  private isDepartmentSelected(departmentId: string) {
    return this.selectedDepartmentsIds.find((selectedDepartment) => selectedDepartment === departmentId)
  }

  private reloadWindow() {
    window.location.reload()
  }

  // ⁡⁢⁣⁣➎ 𝗪𝗮𝘁𝗰𝗵𝗲𝗿𝘀⁡
  @Watch('contextDepartments')
  private onContextDepartmentsChange(value: FetchMeQuery['me']['contextDepartments']) {
    const allAs24DepartmentsSaved = this.as24DepartmentIds?.every((departmentId) => {
      return value?.find((department) => department?.id === departmentId)
    })
    const allTotalDepartmentsSaved = this.totalDepartmentIds?.every((departmentId) => {
      return value?.find((department) => department?.id === departmentId)
    })

    const allCircleKDepartmentsSaved = this.circleKDepartmentIds?.every((departmentId) => {
      return value?.find((department) => department?.id === departmentId)
    })

    if (allAs24DepartmentsSaved || allTotalDepartmentsSaved || allCircleKDepartmentsSaved) {
      const selectedValues = []

      if (allAs24DepartmentsSaved) {
        selectedValues.push(ALL_AS24_DEPARTMENTS_KEY)
      }

      if (allTotalDepartmentsSaved) {
        selectedValues.push(ALL_TOTAL_DEPARTMENTS_KEY)
      }

      if (allCircleKDepartmentsSaved) {
        selectedValues.push(ALL_CIRCLEK_DEPARTMENTS_KEY)
      }

      if (selectedValues.includes(ALL_AS24_DEPARTMENTS_KEY) && selectedValues.includes(ALL_TOTAL_DEPARTMENTS_KEY) && selectedValues.includes(ALL_CIRCLEK_DEPARTMENTS_KEY)) {
        selectedValues.push(ALL_DEPARTMENTS_KEY)
      }

      this.selectedDepartments = selectedValues

      return
    }

    if (value?.length) {
      this.selectedDepartments = [
        ...value.map((department) => department?.id as string)
      ]

      return
    }

    this.selectedDepartments = [ALL_DEPARTMENTS_KEY]
  }

  @Watch('allDepartments')
  private onAllDepartmentsChange() {
    this.onContextDepartmentsChange(this.contextDepartments)
  }

  // ⁡⁢⁣⁣➏ 𝗛𝗼𝗼𝗸𝘀⁡
  private mounted() {
    this.getAllDepartments()
  }
}
</script>

<style lang="scss">
.context-department {
  outline: 1px solid hsla(0, 0%, 100%, 0.3) !important;
  &.is-disabled {
    pointer-events: none;
  }
  .el-loading-mask {
    background-color: hsl(225, 100%, 10%) !important;
    .el-loading-spinner {
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      top: 0 !important;
      height: 100% !important;
      margin: 0 !important;
    }
  }
}
.context-department-popup {
  .el-menu {
    height: fit-content;
    max-height: 300px;
    overflow-y: scroll;
    .el-menu-item {
      display: flex;
      align-items: center;
      border-bottom: 1px solid hsl(0, 0%, 90%) !important;
    }
    .two-row-menu-item {
      color: inherit !important;
      display: flex;
      flex-direction: column;
      background: none;
      pointer-events: none;
      .top-info {
        color: inherit !important;
        line-height: 1;
      }
      .bottom-info {
        color: inherit !important;
        font-size: 10px;
        font-weight: bold;
        padding-top: 1px;
        line-height: 1;
      }
    }
  }
}

</style>
